import { Box, Card, Flex, Grid, Heading, Skeleton } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PlateView } from 'components/common/plate-view';
import { ReleaseView } from 'components/common/release-view';
import { BattersPovIcon } from 'components/common/tooltip/batters-pov';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IPlateLoc, ITrajectory } from 'lib_ts/interfaces/pitches';
import { IPosition } from 'lib_ts/interfaces/pitches/i-base';
import { CSSProperties, useContext, useMemo } from 'react';

interface IProps {
  // determines plate location and release position
  traj: ITrajectory;
  loading?: boolean;
  dialog?: boolean;

  onUpdatePlate: (plate: IPlateLoc) => void;
  onUpdateRelease: (pos: { px: number; pz: number }) => void;
}

export const BallFlightDesigner = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  const trajPlate = useMemo(
    () => TrajHelper.getPlateLoc(props.traj),
    [props.traj]
  );

  const trajReleasePos = useMemo<IPosition>(
    () => ({
      px: props.traj.px,
      py: props.traj.py,
      pz: props.traj.pz,
    }),
    [props.traj]
  );

  const sliderStyle: CSSProperties = {
    width: 'auto',
    // ensures this is centered horizontally
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '50%',
    transform: 'translate(-50%, 0)',
  };

  const sizerStyle: CSSProperties = {
    height: '300px',
  };

  const controlsStyle: CSSProperties = {
    position: 'absolute',
    top: '310px',
    left: '50%',
    transform: 'translate(-50%, 0)',
  };

  const wrapperStyle: CSSProperties = {
    position: 'relative',
    height: props.dialog ? '300px' : '420px',
  };

  const releaseSliderStyle: CSSProperties = {
    ...sliderStyle,
    left: props.dialog ? '35%' : sliderStyle.left,
  };

  return (
    <ErrorBoundary identifier="BallFlightDesigner">
      <Grid columns={{ initial: '1', lg: '2' }} gap={RADIX.FLEX.GAP.MD}>
        <Skeleton loading={!!props.loading}>
          <Card size="2">
            <Flex gap={RADIX.FLEX.GAP.SM} align="center" mb="2">
              <Heading size={RADIX.HEADING.SIZE.SM}>
                {t('pd.plate-location')}
              </Heading>

              <BattersPovIcon />
            </Flex>

            <Box style={wrapperStyle}>
              <PlateView
                identifier="PDPlateView"
                onUpdate={props.onUpdatePlate}
                sliderStyle={sliderStyle}
                sizerStyle={sizerStyle}
                controlsStyle={props.dialog ? undefined : controlsStyle}
                default_plate_x={trajPlate.plate_x}
                default_plate_z={trajPlate.plate_z}
                portraitControls={props.dialog}
                showControls
                isDesigner
                landscape
              />
            </Box>
          </Card>
        </Skeleton>
        <Skeleton loading={!!props.loading}>
          <Card size="2">
            <Heading mb="2" size={RADIX.HEADING.SIZE.SM}>
              {t('pd.release-position')}
            </Heading>

            <Box style={wrapperStyle}>
              <ReleaseView
                isMetric={!!machine.isMetric}
                px={trajReleasePos.px}
                pz={trajReleasePos.pz}
                onUpdate={props.onUpdateRelease}
                sliderStyle={releaseSliderStyle}
                sizerStyle={sizerStyle}
                controlsStyle={props.dialog ? undefined : controlsStyle}
                portraitControls={props.dialog}
                showControls
              />
            </Box>
          </Card>
        </Skeleton>
      </Grid>
    </ErrorBoundary>
  );
};

// ! deprecate since we never need to build for PD
export const BallFlightDesignerSkeleton = () => {
  return (
    <Grid
      columns={{ initial: '1', lg: '2' }}
      gap={RADIX.FLEX.GAP.MD}
      height="100%"
    >
      <Skeleton loading height="100%">
        <Box />
      </Skeleton>
      <Skeleton loading height="100%">
        <Box />
      </Skeleton>
    </Grid>
  );
};
